import type { QueryClient } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';

/**
 * Portas do módulo da clínica nas telas de sempre (Negócio, Início e Ajustes). Mesma regra
 * de `clinicRoutes.tsx`: com `VITE_CLINIC=off` (pacote da loja) os imports somem do pacote e
 * as portas não desenham nada.
 */
const PARTS =
  import.meta.env.VITE_CLINIC === 'off'
    ? null
    : {
        ClinicaCard: lazy(() =>
          import('./ClinicaCard.js').then((m) => ({ default: m.ClinicaCard })),
        ),
        SaudeRow: lazy(() => import('../saude/SaudeRow.js').then((m) => ({ default: m.SaudeRow }))),
        SaudeArrivalRow: lazy(() =>
          import('../saude/SaudeRow.js').then((m) => ({ default: m.SaudeArrivalRow })),
        ),
        ClinicHome: lazy(() =>
          import('../../pages/clinica/HojePage.js').then((m) => ({ default: m.ClinicaInicio })),
        ),
      };

/**
 * Deixa a Início da clínica pronta antes da troca de contexto: o código e o dia da clínica
 * (sem desenhar nada). Na mesa (tela larga) a Início é outra e busca o que precisa.
 */
export const preloadClinicHome: (queryClient: QueryClient) => void =
  import.meta.env.VITE_CLINIC === 'off'
    ? () => {}
    : (queryClient) => {
        void import('../../pages/clinica/HojePage.js').catch(() => {});
        void import('../../lib/api/clinic.js')
          .then((api) =>
            queryClient.prefetchQuery({
              queryKey: api.CLINIC_TODAY_KEY,
              queryFn: api.getClinicToday,
              staleTime: 20_000,
            }),
          )
          .catch(() => {});
      };

/** Cartão "Sua clínica" na página Negócio. */
export function ClinicaCardGate() {
  if (!PARTS) return null;
  return (
    <Suspense fallback={null}>
      <PARTS.ClinicaCard />
    </Suspense>
  );
}

/** A Início no modo Clínica (fase E): o Hoje da clínica no lugar do palco. */
export function ClinicHomeGate() {
  if (!PARTS) return null;
  return (
    <Suspense fallback={null}>
      <PARTS.ClinicHome />
    </Suspense>
  );
}

/** Linha Saúde em Ajustes › Você. */
export function SaudeRowGate() {
  if (!PARTS) return null;
  return (
    <Suspense fallback={null}>
      <PARTS.SaudeRow />
    </Suspense>
  );
}

/** Linha Saúde no painel de chegada do Início. */
export function SaudeArrivalRowGate({ go }: { go: (href: string) => void }) {
  if (!PARTS) return null;
  return (
    <Suspense fallback={null}>
      <PARTS.SaudeArrivalRow go={go} />
    </Suspense>
  );
}
